import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";
import { Customer, CustomerFormData } from "./customer";
import apiDeduplicationService from "../../utils/apiDeduplication";

class CustomerService {
  // Get all customers with pagination and filtering
  async getCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    customer_type?: string;
    
  }): Promise<ApiResponse<{ customers: Customer[]; pagination: any }>> {
    return apiDeduplicationService.execute(
      "/customers",
      "GET",
      () => enhancedApiClient.get("/customers", { 
        params,
        context: "Fetch Customers",
        component: "CustomerService"
      }),
      params
    );
  }

  // Get customer by ID
  async getCustomer(
    customerId: string
  ): Promise<ApiResponse<Customer>> {
    const response = await enhancedApiClient.get(`/customers/${customerId}`, {
      context: "Fetch Customer By ID",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Create new customer
  async createCustomer(
    customerData: CustomerFormData
  ): Promise<ApiResponse<Customer>> {
    const response = await enhancedApiClient.post("/customers", customerData, {
      context: "Create Customer",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update customer
  async updateCustomer(
    customerId: string,
    customerData: Partial<CustomerFormData>
  ): Promise<ApiResponse<Customer>> {
    const response = await enhancedApiClient.put(
      `/customers/${customerId}`,
      customerData,
      {
        context: "Update Customer",
        component: "CustomerService"
      }
    );
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Delete customer
  async deleteCustomer(
    customerId: string
  ): Promise<ApiResponse<{ message: string }>> {
    const response = await enhancedApiClient.delete(`/customers/${customerId}`, {
      context: "Delete Customer",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customers by type
  async getCustomersByType(
    customerType: string
  ): Promise<ApiResponse<Customer[]>> {
    const response = await enhancedApiClient.get(`/customers?customer_type=${customerType}`, {
      context: "Get Customers By Type",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customers by status
  async getCustomersByStatus(
    status: string
  ): Promise<ApiResponse<Customer[]>> {
    const response = await enhancedApiClient.get(`/customers?status=${status}`, {
      context: "Get Customers By Status",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Search customers
  async searchCustomers(searchTerm: string): Promise<ApiResponse<Customer[]>> {
    const response = await enhancedApiClient.get(`/customers?search=${searchTerm}`, {
      context: "Search Customers",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update customer status
  async updateCustomerStatus(
    customerId: string,
    status: string
  ): Promise<ApiResponse<Customer>> {
    const response = await enhancedApiClient.patch(
      `/customers/${customerId}/status`,
      { status },
      {
        context: "Update Customer Status",
        component: "CustomerService"
      }
    );
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customer statistics
  async getCustomerStats(): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/customers/stats", {
      context: "Get Customer Statistics",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customer orders
  async getCustomerOrders(
    customerId: string
  ): Promise<ApiResponse<any[]>> {
    const response = await enhancedApiClient.get(`/customers/${customerId}/orders`, {
      context: "Get Customer Orders",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customer sites
  async getCustomerSites(
    customerId: string
  ): Promise<ApiResponse<any[]>> {
    const response = await enhancedApiClient.get(`/customers/${customerId}/sites`, {
      context: "Get Customer Sites",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Export customers
  async exportCustomers(params?: {
    format?: string;
    filters?: any;
  }): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/customers/export", {
      params,
      context: "Export Customers",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Import customers
  async importCustomers(file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append("file", file);
    
    const response = await enhancedApiClient.post("/customers/import", formData, {
      context: "Import Customers",
      component: "CustomerService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }
}

export const customerService = new CustomerService();
